'use client';

import { useState, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import DiagnosticWizard from '@/components/DiagnosticWizard';
import './globals.css';
import styles from './page.module.css';

// Force dynamic rendering to prevent SSR issues with useSearchParams
export const dynamic = 'force-dynamic';

// User segment definitions for the entry cards
const USER_SEGMENTS = [
  {
    id: 'no-degree',
    icon: '🎯',
    title: 'No degree, no problem',
    description: 'Discover careers that value skills over credentials',
    color: '#D96704',
  },
  {
    id: 'career-changer',
    icon: '🔄',
    title: 'Ready for a change',
    description: "Hate what you studied? Your skills transfer further than you think",
    color: '#3B82F6',
  },
  {
    id: 'passion-seeker',
    icon: '🎸',
    title: 'Turn passion into career',
    description: "Love music, art, or gaming? Find careers that use what you love",
    color: '#8B5CF6',
  },
  {
    id: 'ai-displaced',
    icon: '🤖',
    title: 'Future-proof your career',
    description: 'Worried about AI? Find roles that grow with technology',
    color: '#10B981',
  },
  {
    id: 'gig-seeker',
    icon: '💼',
    title: 'Freelance & side hustles',
    description: 'Monetize your skills on your own terms',
    color: '#F59E0B',
  },
  {
    id: 'returning',
    icon: '🏠',
    title: 'Returning to work',
    description: 'Been away? Your experience still counts',
    color: '#EC4899',
  },
];

const HOW_IT_WORKS_STEPS = [
  {
    number: '1',
    title: 'Answer a few questions',
    description: 'Tell us about your skills, interests, and what matters to you',
    time: '~5 minutes',
  },
  {
    number: '2',
    title: 'See careers that fit YOU',
    description: 'Get matched with careers based on who you are, not just your resume',
    time: 'Instant results',
  },
  {
    number: '3',
    title: 'Get your pathway',
    description: 'See exactly how to get there—first steps, training, timeline',
    time: 'Actionable plan',
  },
];

// Inner component that uses useSearchParams - must be wrapped in Suspense
function HomeContent() {
  const searchParams = useSearchParams();
  // Initialize showQuiz based on URL params to avoid effect-based state updates
  const [showQuiz, setShowQuiz] = useState(() => searchParams.get('unlocked') === 'true');
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);
  const quizRef = useRef<HTMLDivElement>(null);

  // Check for devtest/unlocked mode from URL params
  const isDevMode = searchParams.get('devtest') === 'true' || searchParams.get('unlocked') === 'true';

  const handleStartQuiz = (segmentId?: string) => {
    if (segmentId) {
      setSelectedSegment(segmentId);
    }
    setShowQuiz(true);
    // Smooth scroll to quiz after a brief delay for state update
    setTimeout(() => {
      quizRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleBackToLanding = () => {
    setShowQuiz(false);
    setSelectedSegment(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main className={styles.main}>
      {/* Landing Content - Hidden when quiz is active */}
      {!showQuiz && (
        <>
          {/* Hero Section */}
          <section className={styles.hero}>
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>
                <span className={styles.heroTitleLine}>Find Your Passion.</span>
                <span className={styles.heroTitleLine}>Forge Your Path.</span>
              </h1>
              <p className={styles.heroSubtitle}>
                Whether you&apos;re starting fresh, switching careers, or turning a hobby into income—discover your next chapter.
              </p>
              <button
                className={styles.heroCta}
                onClick={() => handleStartQuiz()}
              >
                Start Your Discovery
                <span className={styles.ctaArrow}>→</span>
              </button>
              <p className={styles.heroNote}>Free to start • No account required • 5 minutes</p>
            </div>
          </section>

          {/* Who Is This For Section */}
          <section className={styles.segmentsSection}>
            <div className={styles.sectionContainer}>
              <h2 className={styles.sectionTitle}>Who is PathFinder for?</h2>
              <p className={styles.sectionSubtitle}>
                Choose what resonates with you—or just dive in
              </p>

              <div className={styles.segmentsGrid}>
                {USER_SEGMENTS.map((segment) => (
                  <button
                    key={segment.id}
                    className={styles.segmentCard}
                    onClick={() => handleStartQuiz(segment.id)}
                    style={{ '--segment-color': segment.color } as React.CSSProperties}
                  >
                    <span className={styles.segmentIcon}>{segment.icon}</span>
                    <h3 className={styles.segmentTitle}>{segment.title}</h3>
                    <p className={styles.segmentDescription}>{segment.description}</p>
                    <span className={styles.segmentCta}>Start →</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* How It Works Section */}
          <section className={styles.howItWorksSection}>
            <div className={styles.sectionContainer}>
              <h2 className={styles.sectionTitle}>How it works</h2>

              <div className={styles.stepsContainer}>
                {HOW_IT_WORKS_STEPS.map((step, index) => (
                  <div key={index} className={styles.step}>
                    <div className={styles.stepNumber}>{step.number}</div>
                    <div className={styles.stepContent}>
                      <h3 className={styles.stepTitle}>{step.title}</h3>
                      <p className={styles.stepDescription}>{step.description}</p>
                      <span className={styles.stepTime}>{step.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Trust Section */}
          <section className={styles.trustSection}>
            <div className={styles.sectionContainer}>
              <div className={styles.trustContent}>
                <h2 className={styles.trustTitle}>Ready to find your path?</h2>
                <p className={styles.trustSubtitle}>
                  Join thousands discovering careers they never knew existed
                </p>

                <div className={styles.trustBadges}>
                  <span className={styles.trustBadge}>55+ careers mapped</span>
                  <span className={styles.trustBadge}>5-factor matching</span>
                  <span className={styles.trustBadge}>Veteran-friendly</span>
                </div>

                <button
                  className={styles.trustCta}
                  onClick={() => handleStartQuiz()}
                >
                  Start Your Discovery
                  <span className={styles.ctaArrow}>→</span>
                </button>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className={styles.footer}>
            <div className={styles.footerContent}>
              <p className={styles.footerBrand}>
                <span className={styles.footerLogo}>PathFinder</span>
                <span className={styles.footerDivider}>•</span>
                <span className={styles.footerTagline}>A Tangent Forge Product</span>
              </p>
              <p className={styles.footerLinks}>
                <a href="https://tangentforge.com" target="_blank" rel="noopener noreferrer">
                  tangentforge.com
                </a>
              </p>
            </div>
          </footer>
        </>
      )}

      {/* Quiz Section */}
      {showQuiz && (
        <div ref={quizRef} className={styles.quizSection}>
          <div className={styles.quizHeader}>
            <button
              className={styles.backButton}
              onClick={handleBackToLanding}
            >
              ← Back
            </button>
            <div className={styles.quizBranding}>
              <span className={styles.quizLogo}>PathFinder</span>
            </div>
          </div>
          <DiagnosticWizard initialSegment={selectedSegment} devMode={isDevMode} />
        </div>
      )}
    </main>
  );
}

// Wrap HomeContent in Suspense for useSearchParams (Next.js 16 requirement)
export default function Home() {
  return (
    <Suspense fallback={
      <main className={styles.main}>
        <div className={styles.loading}>Loading...</div>
      </main>
    }>
      <HomeContent />
    </Suspense>
  );
}
